import { useEffect, useRef } from "react";

import { ActionIcon, FileButton, Flex, Tooltip, UnstyledButton } from "@mantine/core";
import { IconFileText, IconPlus } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Text } from "components";
import { IRootState } from "config/redux";
import { ServiceConstants } from "constant";
import { BankSelectionService } from "services";

import { buildBankAttachmentUrl } from "../bank-selection/utils";

const {
  REQUEST_BANK_ATTACHMENT_SUMMARY_GET_ID,
  REQUEST_BANK_ATTACHMENT_SUMMARY_UPLOAD_ID,
  REQUEST_PENDING,
  REQUEST_SUCCESS,
} = ServiceConstants;

interface BankRequestDocumentProps {
  bankCreditRequestId: number;
}

export const BankRequestDocument = ({ bankCreditRequestId }: BankRequestDocumentProps) => {
  const { t } = useTranslation();
  const resetFile = useRef<(() => void) | null>(null);
  const getResponse = useSelector(
    (state: IRootState) =>
      state.service.object[REQUEST_BANK_ATTACHMENT_SUMMARY_GET_ID + bankCreditRequestId]
  );
  const uploadResponse = useSelector(
    (state: IRootState) =>
      state.service.object[REQUEST_BANK_ATTACHMENT_SUMMARY_UPLOAD_ID + bankCreditRequestId]
  );
  const bankDocument =
    uploadResponse?.status === REQUEST_SUCCESS
      ? uploadResponse.data
      : getResponse?.data?.attachment;
  const isUploading = uploadResponse?.status === REQUEST_PENDING;

  useEffect(() => {
    BankSelectionService.bankAttachmentSummaryGet(bankCreditRequestId);
  }, [bankCreditRequestId]);

  const handleUpload = (file: File | null) => {
    resetFile.current?.();
    if (file) {
      BankSelectionService.bankAttachmentSummaryUpload(bankCreditRequestId, file);
    }
  };

  const handleOpen = () => {
    if (!bankDocument) return;

    const url = buildBankAttachmentUrl(bankDocument, bankCreditRequestId);
    if (url) {
      window.open(url, "_blank");
    }
  };

  return (
    <Flex ml="auto" mr="sm" align="center">
      {bankDocument ? (
        <Tooltip label={t("bankCredit.header.bankDocument.open")}>
          <UnstyledButton
            onClick={handleOpen}
            p="xs"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--mantine-spacing-xs)",
              maxWidth: "14rem",
              border: "1px solid var(--mantine-color-gray-3)",
              borderRadius: "var(--mantine-radius-sm)",
              backgroundColor: "var(--mantine-color-white)",
            }}
          >
            <IconFileText size={16} color="#B046AC" />
            <Text
              size="sm"
              style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {bankDocument.name}
            </Text>
          </UnstyledButton>
        </Tooltip>
      ) : (
        <FileButton
          onChange={handleUpload}
          accept="application/pdf"
          disabled={isUploading}
          resetRef={resetFile}
        >
          {(props) => (
            <Tooltip label={t("bankCredit.header.bankDocument.add")}>
              <ActionIcon
                {...props}
                variant="default"
                size="lg"
                loading={isUploading}
                aria-label={t("bankCredit.header.bankDocument.add")}
              >
                <IconPlus size={20} />
              </ActionIcon>
            </Tooltip>
          )}
        </FileButton>
      )}
    </Flex>
  );
};
